import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { visibleForAI } from "../lib/uploadFilters";
import InlinePluginPicker from "./InlinePluginPicker";
import PluginPromptsPanel from "./PluginPromptsPanel";
import PromptPreviewDialog from "./PromptPreviewDialog";
import { useAuth } from "../lib/auth";
import { previewPluginFragment } from "../lib/api";
import { useFeatureFlags } from "../lib/feature_flags";
import { toast } from "sonner";
import {
    Brain,
    Sparkles,
    FileText,
    Search,
    SlidersHorizontal,
    Pin,
} from "lucide-react";

export const TRADES = [
    { id: "excavation", label: "Excavation" },
    { id: "sitework", label: "Sitework / Civil" },
    { id: "demolition", label: "Demolition" },
    { id: "foundations", label: "Foundations" },
    { id: "concrete", label: "Concrete" },
    { id: "masonry", label: "Masonry" },
    { id: "framing", label: "Framing" },
    { id: "structural_steel", label: "Structural Steel" },
    { id: "roofing", label: "Roofing (sloped)" },
    { id: "roofing_flat", label: "Roofing (flat / commercial)" },
    { id: "insulation", label: "Insulation" },
    { id: "drywall", label: "Drywall" },
    { id: "flooring", label: "Flooring" },
    { id: "painting", label: "Painting" },
    { id: "windows_doors", label: "Windows & Doors" },
    { id: "electrical", label: "Electrical" },
    { id: "plumbing", label: "Plumbing" },
    { id: "hvac", label: "HVAC" },
    { id: "septic", label: "Septic" },
    { id: "landscaping", label: "Landscaping" },
    { id: "other", label: "Other / General" },
];

const STYLES = [
    { id: "technical", title: "Technical", desc: "For estimators / licensed trades" },
    { id: "layman", title: "Layman", desc: "Everyday language, no jargon" },
    { id: "moron", title: "Simple", desc: "Explain it like I'm 5" },
];

export default function AnalyzeDialog({
    open,
    onOpenChange,
    files = [],
    onSubmit,
    projectId,
}) {
    // iter-170: plugin toggles now live inside this dialog.
    const [activePluginIds, setActivePluginIds] = useState([]);
    // iter-40: scope/trade/multi-trade selection was removed. The job's
    // scope is now driven entirely by the per-project active plugins
    // toggled in the Workspace floating bar. These state vars are kept
    // at safe defaults so the backend's legacy fields still have values.
    const scope = "overall";
    const selectedTrades = new Set();
    const [specificSearch, setSpecificSearch] = useState("");
    const [docMode, setDocMode] = useState("all"); // "all" | "specific"
    const [selectedDocs, setSelectedDocs] = useState(new Set());
    const [style, setStyle] = useState("technical");
    const [autoPin, setAutoPin] = useState(false);
    const flags = useFeatureFlags();
    // iter-43: per-plugin prompt state owned by the parent dialog.
    const [pluginPrompts, setPluginPrompts] = useState({
        checkedIds: [],
        adhoc: {},
    });
    // iter-48: superadmin dev-only prompt preview.
    const { user } = useAuth();
    const isSuperadmin = user?.role === "superadmin";
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFragment, setPreviewFragment] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);

    // iter-167 — Broadened from PDF-only to any visual document so
    // projects made of iPhone photos (HEIC), scans (PNG/JPG) etc.
    // can be analyzed too. Backend does Claude-vision OCR on images.
    const analyzableFiles = visibleForAI(
        files.filter((f) => {
            const ct = (f.content_type || "").toLowerCase();
            const name = (f.original_filename || "").toLowerCase();
            if (ct === "application/pdf" || name.endsWith(".pdf")) return true;
            if (ct.startsWith("image/")) return true;
            return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(name);
        }),
    );

    useEffect(() => {
        if (!open) return;
        setSpecificSearch("");
        setDocMode("all");
        setSelectedDocs(new Set());
        setStyle("technical");
        setAutoPin(false);
        setPluginPrompts({ checkedIds: [], adhoc: {} });
    }, [open]);

    const toggleDoc = (id) => {
        setSelectedDocs((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const canSubmit =
        analyzableFiles.length > 0 &&
        (docMode === "all" || selectedDocs.size > 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        const adhocList = Object.entries(pluginPrompts.adhoc || {})
            .map(([plugin_id, text]) => ({
                plugin_id,
                text: (text || "").trim(),
            }))
            .filter((e) => e.text && (activePluginIds || []).includes(e.plugin_id));
        const payload = {
            scope,
            trade: null,
            trades: [],
            plugin_ids: Array.from(activePluginIds || []),
            plugin_prompt_ids: pluginPrompts.checkedIds || [],
            plugin_adhoc_prompts: adhocList,
            plugin_prompt_inputs: pluginPrompts.inputs || {},
            specific_search: specificSearch.trim(),
            document_ids:
                docMode === "specific" ? Array.from(selectedDocs) : null,
            style,
            auto_pin: flags.show_auto_pin ? autoPin : false,
        };
        // iter-48: superadmin gets a prompt preview before the run.
        if (isSuperadmin) {
            setPreviewLoading(true);
            try {
                const { fragment } = await previewPluginFragment({
                    kind: "analyze",
                    plugin_ids: payload.plugin_ids,
                    plugin_prompt_ids: payload.plugin_prompt_ids,
                    plugin_adhoc_prompts: payload.plugin_adhoc_prompts,
                    plugin_prompt_inputs: payload.plugin_prompt_inputs,
                });
                setPreviewFragment(fragment || "");
                setPendingPayload(payload);
                setPreviewOpen(true);
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail ||
                        "Couldn't build prompt preview — running anyway.",
                );
                onOpenChange(false);
                onSubmit && onSubmit(payload);
            } finally {
                setPreviewLoading(false);
            }
            return;
        }
        onOpenChange(false);
        onSubmit && onSubmit(payload);
    };

    const handlePreviewRun = () => {
        setPreviewOpen(false);
        onOpenChange(false);
        const p = pendingPayload;
        setPendingPayload(null);
        if (p) onSubmit && onSubmit(p);
    };
    const handlePreviewAbort = () => {
        setPreviewOpen(false);
        setPendingPayload(null);
        setPreviewFragment("");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#141414] border border-white/15 text-white w-[calc(100vw-1.5rem)] max-w-[620px] max-h-[90vh] overflow-hidden p-4 sm:p-6"
                data-testid="analyze-dialog"
            >
                <div className="overflow-y-auto max-h-[calc(90vh-2rem)] min-w-0 flex flex-col gap-4">
                    <DialogHeader className="min-w-0">
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Brain className="w-5 h-5 text-[#8fa4f0]" />
                            Analyze plans
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            AI-powered scope extraction from the drawings.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* iter-170: plugin picker at the top of the form. */}
                        <InlinePluginPicker
                            projectId={projectId}
                            onChange={setActivePluginIds}
                            testIdPrefix="analyze-plugin-picker"
                        />

                        {/* iter-43: per-plugin custom prompt boxes. */}
                        <PluginPromptsPanel
                            activePluginIds={activePluginIds}
                            kind="analyze"
                            value={pluginPrompts}
                            onChange={setPluginPrompts}
                            testIdPrefix="analyze-plugin-prompts"
                        />

                        {/* Documents */}
                        <section>
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold block mb-2">
                                <FileText className="w-3 h-3 inline mr-1 text-[#8fa4f0]" />
                                Documents
                            </Label>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                {["all", "specific"].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => setDocMode(m)}
                                        data-testid={`analyze-docmode-${m}`}
                                        className={`rounded-sm border px-3 py-2 text-left transition-colors ${
                                            docMode === m
                                                ? "border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-white"
                                                : "border-white/15 hover:border-[#1a3fd1]/60 text-slate-300"
                                        }`}
                                    >
                                        <div className="text-sm font-semibold">
                                            {m === "all" ? "All documents" : "Specific"}
                                        </div>
                                        <div className="text-[11px] text-slate-400">
                                            {m === "all"
                                                ? `${analyzableFiles.length} PDFs`
                                                : `${selectedDocs.size} selected`}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            {docMode === "specific" && (
                                <div
                                    className="max-h-40 overflow-y-auto rounded-sm border border-white/15 bg-[#0a0a0a] divide-y divide-white/10"
                                    data-testid="analyze-doc-list"
                                >
                                    {analyzableFiles.length === 0 && (
                                        <div className="p-3 text-xs text-slate-400">
                                            No PDF documents available.
                                        </div>
                                    )}
                                    {analyzableFiles.map((f) => (
                                        <label
                                            key={f.id}
                                            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-white/10"
                                        >
                                            <Checkbox
                                                checked={selectedDocs.has(f.id)}
                                                onCheckedChange={() => toggleDoc(f.id)}
                                                data-testid={`analyze-doc-${f.id}`}
                                                className="border-slate-500 data-[state=checked]:bg-[#1a3fd1] data-[state=checked]:border-[#1a3fd1]/50"
                                            />
                                            <span className="text-sm text-white truncate flex-1">
                                                {f.original_filename}
                                            </span>
                                            {f.page_count > 0 && (
                                                <span className="text-[10px] text-slate-500 tabular-nums">
                                                    {f.page_count}p
                                                </span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Specific search */}
                        <section>
                            <Label
                                htmlFor="analyze-specific-search"
                                className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold block mb-2"
                            >
                                <Search className="w-3 h-3 inline mr-1 text-[#8fa4f0]" />
                                Specific search (optional)
                            </Label>
                            <Input
                                id="analyze-specific-search"
                                value={specificSearch}
                                onChange={(e) => setSpecificSearch(e.target.value)}
                                placeholder="e.g. Call out every egress door spec"
                                data-testid="analyze-specific-search"
                                className="bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]"
                            />
                        </section>

                        {/* Output style */}
                        <section>
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold block mb-2">
                                <SlidersHorizontal className="w-3 h-3 inline mr-1 text-[#8fa4f0]" />
                                Output style
                            </Label>
                            <div className="grid grid-cols-3 gap-2">
                                {STYLES.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => setStyle(opt.id)}
                                        data-testid={`analyze-style-${opt.id}`}
                                        className={`rounded-sm border p-2 text-left transition-colors ${
                                            style === opt.id
                                                ? "border-[#1a3fd1]/50 bg-[#1a3fd1]/15 text-white"
                                                : "border-white/15 hover:border-[#1a3fd1]/60 text-slate-300"
                                        }`}
                                    >
                                        <div className="text-xs font-bold uppercase tracking-wide">
                                            {opt.title}
                                        </div>
                                        <div className="text-[10px] text-slate-400 leading-tight mt-0.5">
                                            {opt.desc}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>

                        {/* Progress is now a fullscreen video overlay handled by the parent. */}

                        <label
                            htmlFor="analyze-auto-pin"
                            className={`flex items-start gap-3 rounded-sm border border-white/15 bg-white/[0.05] p-3 cursor-pointer hover:border-[#1a3fd1]/50 transition-colors ${flags.show_auto_pin ? "" : "hidden"}`}
                        >
                            <Checkbox
                                id="analyze-auto-pin"
                                checked={autoPin}
                                onCheckedChange={(v) => setAutoPin(v === true)}
                                data-testid="analyze-auto-pin"
                                className="mt-0.5 border-white/15 data-[state=checked]:bg-[#1a3fd1] data-[state=checked]:border-[#1a3fd1]/50"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-white">
                                    <Pin className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                    Auto-pin applicable pages
                                </div>
                                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                                    When the analysis finishes, every document fed into it (and its split pages) will be pinned to the top of your sidebar.
                                </p>
                            </div>
                        </label>

                        <div className="flex items-center justify-end gap-2 pt-1">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="btn-analyze-cancel"
                                className="text-slate-300 hover:text-white hover:bg-white/10"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="btn-analyze-submit"
                                className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-bold disabled:opacity-40 disabled:bg-[#1a1a1a]"
                            >
                                <Sparkles className="w-4 h-4 mr-2" />
                                Submit
                            </Button>
                        </div>
                    </form>
                </div>
            </DialogContent>
            <PromptPreviewDialog
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                fragment={previewFragment}
                kind="analyze"
                activePluginIds={activePluginIds}
                onRun={handlePreviewRun}
                onAbort={handlePreviewAbort}
                loading={previewLoading}
            />
        </Dialog>
    );
}
